import { useEffect, useState } from "react";
import type { GameSummary } from "@msf/shared";
import { api } from "../lib/api.js";
import { useSession } from "../store/session.js";
import { useRouter, Link } from "../lib/router.js";
import { Button, Screen } from "../components/ui.js";
import { ChevronLeftIcon, CraterIcon, RefreshIcon } from "../components/icons.js";

export function Lobby() {
  const { token } = useSession();
  const { navigate } = useRouter();
  const [games, setGames] = useState<GameSummary[] | null>(null);
  const [joining, setJoining] = useState<string | null>(null);

  async function refresh() {
    try {
      setGames(await api.listPublicGames());
    } catch {
      setGames([]);
    }
  }

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 4000);
    return () => clearInterval(t);
  }, []);

  async function join(g: GameSummary) {
    if (!token) return navigate("/");
    setJoining(g.id);
    try {
      await api.joinGame(g.id, token);
      navigate(`/g/${g.id}`);
    } catch {
      setJoining(null);
      refresh();
    }
  }

  return (
    <Screen className="py-6">
      <Link to="/" className="mb-4 inline-flex items-center gap-1 text-sm font-bold opacity-60">
        <ChevronLeftIcon className="h-4 w-4" /> Back
      </Link>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-black">Public games</h1>
        <button onClick={refresh} className="clay-btn px-3 py-1.5" aria-label="Refresh">
          <RefreshIcon className="h-4 w-4" />
        </button>
      </div>

      {games === null && <p className="opacity-60">Loading…</p>}
      {games?.length === 0 && (
        <div className="clay mt-6 p-6 text-center">
          <CraterIcon className="mx-auto h-14 w-14" />
          <p className="mt-2 font-bold">No open games right now</p>
          <p className="mt-1 text-sm opacity-60">Be the first — create one!</p>
          <Button variant="primary" className="mt-4" onClick={() => navigate("/create")}>
            Create a game
          </Button>
        </div>
      )}

      <ul className="space-y-2">
        {games?.map((g) => (
          <li key={g.id} className="clay flex items-center justify-between p-3">
            <div>
              <p className="font-black">{g.hostNickname}</p>
              <p className="text-xs opacity-60">Waiting for an opponent · code {g.joinCode}</p>
            </div>
            <Button variant="primary" disabled={joining === g.id} onClick={() => join(g)}>
              {joining === g.id ? "Joining…" : "Join"}
            </Button>
          </li>
        ))}
      </ul>
    </Screen>
  );
}
